import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { OrganizationUser } from "schemas/dashboard";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";
import { formatEpochTimestamp } from "utils/time";

import { AdminAddOnItem } from "./schema";

interface Props {
  setAlert: SetAlertType;
  organizationUser: OrganizationUser | null;
}

const AdminAddOns = ({ organizationUser, setAlert }: Props) => {
  const { getToken } = useAuth();
  const [userPurchases, setUserPurchases] = useState<AdminAddOnItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [emailLoading, setEmailLoading] = useState(false);

  const fetchUserPurchases = async () => {
    if (!organizationUser) return;
    try {
      setLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/add-ons`,
        "GET",
      );
      setUserPurchases(res.userPurchases);
    } catch (error) {
      setAlert(error.message || "Unable to fetch user purchases", "error");
    } finally {
      setLoading(false);
    }
  };

  const sendTemplateCompletedEmail = async () => {
    try {
      setEmailLoading(true);
      await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/add-ons/send-completed-template-email`,
        "POST",
      );
      setAlert("Successfully sent email.", "success");
    } catch (error) {
      setAlert(error.message || "Unable to send email", "error");
    } finally {
      setEmailLoading(false);
    }
  };

  const toggleIsFinalized = async (addOnId: number, finalized: boolean) => {
    try {
      setLoading(true);
      await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/add-ons/${addOnId}/finalized`,
        "PUT",
        {},
        {
          finalized,
        },
      );
      fetchUserPurchases();
      setAlert("Successfully updated repitching add-on", "success");
    } catch (error) {
      setAlert(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUser?.id]);
  return (
    <>
      <Typography variant="h6">Add-Ons Purchases</Typography>

      <LoadingButton
        size="small"
        variant="contained"
        disableElevation
        loading={emailLoading}
        sx={{ mb: 1 }}
        onClick={sendTemplateCompletedEmail}
      >
        Send Template Completed Email
      </LoadingButton>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer>
          <Table sx={{ maxWidth: 800 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Date Purchased</TableCell>
                <TableCell align="right">Item Name</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Purchase Details</TableCell>
                <TableCell align="right">Domain Info</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userPurchases.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {formatEpochTimestamp({
                      epochTimestamp: row?.createdAt,
                      fullDate: true,
                    })}
                  </TableCell>
                  <TableCell align="right">{row.itemName}</TableCell>
                  <TableCell align="right">{row.quantity}</TableCell>
                  <TableCell align="right">
                    {row?.document && (
                      <Link
                        target="_blank"
                        sx={{ cursor: "pointer" }}
                        href={row.document}
                      >
                        View Google Doc
                      </Link>
                    )}

                    {row?.purchaseDetails?.review !== undefined && (
                      <>
                        <strong>Review Email Before Sent:</strong>{" "}
                        {row?.purchaseDetails?.review ? "Yes" : "No"}
                        <br />
                      </>
                    )}

                    {row?.purchaseDetails?.followups !== undefined && (
                      <>
                        <strong>Number of Followups:</strong>{" "}
                        {row?.purchaseDetails?.followups}
                      </>
                    )}

                    {(row.itemName === "repitching_tier_one" ||
                      row.itemName === "repitching_tier_two" ||
                      row.itemName === "repitching_tier_three") && (
                      <Box>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              onChange={() => {
                                toggleIsFinalized(
                                  row.id,
                                  !!!row?.purchaseDetails?.finalized,
                                );
                              }}
                              checked={!!row?.purchaseDetails?.finalized}
                            />
                          }
                          label="Is Finalized"
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {row?.domainInfo && (
                      <>
                        Email: {row.domainInfo.username}@{row.domainInfo.domain}
                        <br />
                        First Name: {row.domainInfo.first_name}
                        <br />
                        Last Name: {row.domainInfo.last_name}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default AdminAddOns;
